import { useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useOutsideClick } from '../hooks/useOutsideClick'

const NAV_LINKS = [
  { to: '/devices', label: 'Devices' },
  { to: '/accounts', label: 'Accounts' },
  { to: '/reservations', label: 'Reservations' },
  { to: '/bundles', label: 'Bundles' },
]

function NavLinks({ onClick }) {
  return NAV_LINKS.map(({ to, label }) => (
    <NavLink
      key={to}
      to={to}
      onClick={onClick}
      className={({ isActive }) =>
        'px-3 py-1.5 rounded-md text-sm font-medium transition-colors ' +
        (isActive
          ? 'text-primary bg-primary/10 ring-1 ring-primary/20'
          : 'text-base-content/45 hover:text-base-content/80 hover:bg-base-300/30')
      }
    >
      {label}
    </NavLink>
  ))
}

export default function Navbar({ user, onLogout }) {
  const email = user?.email ?? ''
  const initial = email[0]?.toUpperCase() ?? '?'
  const [navOpen, setNavOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const navRef = useOutsideClick(() => setNavOpen(false), navOpen)
  const menuRef = useOutsideClick(() => setMenuOpen(false), menuOpen)

  return (
    <header className="sticky top-0 z-30 bg-base-200/80 backdrop-blur border-b border-base-300/60 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto flex items-center h-14 gap-6">
        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1 flex-1">
          <NavLinks />
        </nav>

        {/* Mobile hamburger */}
        <div className={`dropdown lg:hidden flex-1 ${navOpen ? 'dropdown-open' : ''}`} ref={navRef}>
          <button
            type="button"
            className="btn btn-ghost btn-sm btn-square"
            aria-label="Navigation menu"
            aria-haspopup="true"
            aria-expanded={navOpen}
            onClick={() => setNavOpen(o => !o)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <ul className="menu menu-sm dropdown-content bg-base-200 border border-base-300 rounded-box mt-3 w-48 p-2 shadow-xl z-50">
            {NAV_LINKS.map(({ to, label }) => (
              <li key={to}>
                <NavLink
                  to={to}
                  onClick={() => setNavOpen(false)}
                  className={({ isActive }) => isActive ? 'bg-base-300' : ''}
                >
                  {label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>

        {/* User dropdown */}
        <div className={`dropdown dropdown-end shrink-0 ${menuOpen ? 'dropdown-open' : ''}`} ref={menuRef}>
          <button
            type="button"
            className="btn btn-ghost btn-circle w-8 h-8 min-h-0 bg-primary/15 text-primary font-semibold text-sm"
            aria-label="Account menu"
            aria-haspopup="true"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(o => !o)}
          >
            {initial}
          </button>
          <ul className="dropdown-content bg-base-200 border border-base-300 rounded-box mt-3 w-56 shadow-xl z-50 py-2">
            <li className="px-4 py-2 mb-1">
              <span className="text-xs text-base-content/40 block truncate">{email}</span>
            </li>
            <li className="border-t border-base-300/60 mt-1 pt-1">
              <button
                className="w-full text-left px-4 py-2 text-sm text-error hover:bg-error/10 transition-colors"
                onClick={() => { setMenuOpen(false); onLogout() }}
              >
                Sign out
              </button>
            </li>
          </ul>
        </div>
      </div>
    </header>
  )
}
